import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import api from '../api/API';
import { useWorkspace } from '../context/WorkspaceContext';
import './ProjetosPage.css';

const LIMIT = 50;

const DEFAULT_PROJECT = {
  descricao: '',
  detalhe: '',
  data_prazo: '',
  cor_hexa: '#4f46e5',
  cor_fonte_hexa: '#ffffff',
};

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) throw new Error(response?.data?.message || 'Falha ao carregar dados.');
  return response?.data?.records || response?.data?.data || [];
};

const ProjetosPage = () => {
  const [projetos, setProjetos] = useState([]);
  const [totalProjetos, setTotalProjetos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [novoProjeto, setNovoProjeto] = useState(DEFAULT_PROJECT);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const {
    selectedWorkspace,
    selectedWorkspaceId,
    workspaces,
    loading: loadingWorkspaces,
    error: workspaceError,
  } = useWorkspace();

  const isEditing = Boolean(editingProjectId);

  const workspaceOptions = useMemo(
    () => workspaces.filter((workspace) => workspace?.id),
    [workspaces]
  );

  useEffect(() => {
    if (!selectedWorkspaceId) {
      setProjetos([]);
      setTotalProjetos(0);
      return;
    }

    carregarProjetos(true);
  }, [selectedWorkspaceId]);

  const carregarProjetos = async (reset = false) => {
    if (!selectedWorkspaceId || loading) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const queryOffset = reset ? 0 : offset;
      const response = await api.get(
        `/projects/summary?workspaceId=${selectedWorkspaceId}&limit=${LIMIT}&offset=${queryOffset}`
      );
      const data = parseCollection(response);

      if (reset) {
        setProjetos(data);
        setOffset(LIMIT);
      } else {
        setProjetos((prev) => [...prev, ...data]);
        setOffset((prev) => prev + LIMIT);
      }

      setHasMore(data.length >= LIMIT);

      const totalResponse = await api.get(`/projects/count?workspaceId=${selectedWorkspaceId}`);
      setTotalProjetos(totalResponse.data?.total ?? data.length);
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Nao foi possivel carregar os projetos.'
      );
    } finally {
      setLoading(false);
    }
  };

  const criarProjeto = async () => {
    if (!novoProjeto.id_workspace) {
      setErrorMessage('Selecione um workspace antes de criar um projeto.');
      return;
    }

    if (!novoProjeto.descricao.trim()) {
      setErrorMessage('Informe o nome do projeto antes de criar.');
      return;
    }

    try {
      const payload = {
        fields: {
          ...novoProjeto,
          id_workspace: Number(novoProjeto.id_workspace),
        },
      };

      if (editingProjectId) {
        await api.put(`/projects/${editingProjectId}`, payload);
      } else {
        await api.post('/projects', payload);
      }

      setShowModal(false);
      setNovoProjeto({
        ...DEFAULT_PROJECT,
        id_workspace: selectedWorkspaceId || '',
      });
      setEditingProjectId(null);
      carregarProjetos(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          `Nao foi possivel ${editingProjectId ? 'atualizar' : 'criar'} o projeto.`
      );
    }
  };

  const openCreateModal = () => {
    setErrorMessage('');
    setEditingProjectId(null);
    setNovoProjeto({
      ...DEFAULT_PROJECT,
      id_workspace: selectedWorkspaceId || '',
    });
    setShowModal(true);
  };

  const openEditModal = (projeto, event) => {
    event.stopPropagation();
    setErrorMessage('');
    setEditingProjectId(projeto.id);
    setNovoProjeto({
      descricao: projeto.descricao || '',
      detalhe: projeto.detalhe || '',
      data_prazo: projeto.data_prazo ? String(projeto.data_prazo).slice(0, 10) : '',
      cor_hexa: projeto.cor_hexa || DEFAULT_PROJECT.cor_hexa,
      cor_fonte_hexa: projeto.cor_fonte_hexa || DEFAULT_PROJECT.cor_fonte_hexa,
      id_workspace: String(projeto.id_workspace || selectedWorkspaceId || ''),
    });
    setShowModal(true);
  };

  return (
    <Container fluid className="projetos-container">
      <div className="projetos-header">
        <div>
          <h2>Projetos</h2>
          <p>
            {selectedWorkspace
              ? `${totalProjetos} projetos em ${selectedWorkspace.nome}`
              : 'Selecione um workspace para visualizar os projetos'}
          </p>
        </div>
        <Button className="btn-modern" onClick={openCreateModal} disabled={!selectedWorkspaceId}>
          + Novo Projeto
        </Button>
      </div>

      {workspaceError && (
        <Alert variant="warning" className="mb-4">
          {workspaceError}
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-4">
          {errorMessage}
        </Alert>
      )}

      {!selectedWorkspaceId && !loadingWorkspaces && (
        <div className="workspace-empty-state">
          Nenhum workspace disponivel para carregar projetos.
        </div>
      )}

      <Row>
        {projetos.map((projeto) => {
          const percentual = projeto.total_tarefas
            ? Math.round((projeto.total_concluidas / projeto.total_tarefas) * 100)
            : 0;

          return (
            <Col key={projeto.id} xs={12} md={6} lg={4} xl={3} className="mb-4">
              <Card
                className="project-card-modern"
                style={{
                  backgroundColor: projeto.cor_hexa,
                  color: projeto.cor_fonte_hexa,
                }}
                onClick={() => navigate(`/projetos/${projeto.id}`)}
              >
                <Card.Body>
                  <button
                    type="button"
                    className="project-edit-button"
                    onClick={(event) => openEditModal(projeto, event)}
                    aria-label={`Editar projeto ${projeto.descricao}`}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>

                  <div className="project-workspace-chip">
                    {selectedWorkspace?.nome || projeto.workspace_nome || 'Workspace'}
                  </div>

                  <h5>{projeto.descricao}</h5>
                  <p className="project-detail">{projeto.detalhe}</p>

                  <div className="project-meta">
                    <small>
                      {projeto.total_concluidas} / {projeto.total_tarefas} tarefas
                    </small>
                    {projeto.data_prazo && (
                      <small>
                        Prazo: {new Date(projeto.data_prazo).toLocaleDateString()}
                      </small>
                    )}
                  </div>

                  <ProgressBar now={percentual} className="progress-modern" />
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {!loading && selectedWorkspaceId && projetos.length === 0 && (
        <div className="workspace-empty-state">
          Nenhum projeto encontrado para o workspace selecionado.
        </div>
      )}

      {hasMore && selectedWorkspaceId && (
        <div className="text-center mt-4">
          <Button onClick={() => carregarProjetos()} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Carregar mais'}
          </Button>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingProjectId(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Editar Projeto' : 'Novo Projeto'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Workspace</Form.Label>
              <Form.Select
                value={novoProjeto.id_workspace || ''}
                onChange={(e) => setNovoProjeto({ ...novoProjeto, id_workspace: e.target.value })}
              >
                <option value="">Selecione</option>
                {workspaceOptions.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.nome}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                value={novoProjeto.descricao}
                onChange={(e) => setNovoProjeto({ ...novoProjeto, descricao: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descricao</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={novoProjeto.detalhe}
                onChange={(e) => setNovoProjeto({ ...novoProjeto, detalhe: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Prazo</Form.Label>
              <Form.Control
                type="date"
                value={novoProjeto.data_prazo}
                onChange={(e) => setNovoProjeto({ ...novoProjeto, data_prazo: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cor do Card</Form.Label>
              <Form.Control
                type="color"
                value={novoProjeto.cor_hexa}
                onChange={(e) => setNovoProjeto({ ...novoProjeto, cor_hexa: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button className="btn-modern" onClick={criarProjeto}>
            {isEditing ? 'Salvar alteracoes' : 'Criar Projeto'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjetosPage;
